import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppDispatch } from '../store/hooks';
import { fetchCategories } from '../store/categorySlice';
import TaskForm from './TaskForm';
import TaskListView from './TaskListView';
import CategoryManagement from './CategoryManagement';
import CompletedTasks from './CompletedTasks';
import {
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { logout } from '../api/auth';
import '../app.css';

const ProtectedApp: React.FC = () => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load categories on app start (available for all components)
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await logout();
    } catch (err) {
      window.location.href = '/.auth/logout';
    }
  };

  const menuItems = [
    { text: 'Active Tasks', path: '/tasks-list' },
    { text: 'Completed Tasks', path: '/completed-tasks' },
    { text: 'Categories', path: '/categories' },
    { text: 'Add Task', path: '/add-task' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
      <Typography
        variant="h6"
        sx={{
          my: 2,
          textAlign: 'center',
          color: import.meta.env.DEV ? '#ffeb3b' : 'inherit'
        }}
      >
        {import.meta.env.VITE_APP_NAME}
      </Typography>
      <List sx={{ pt: 0, flex: 0 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton component={Link} to={item.path} sx={{ textAlign: 'center' }}>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ textAlign: 'center' }}>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <div>
      <AppBar position="fixed" sx={{ top: 0, left: 0, right: 0 }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              color: import.meta.env.DEV ? '#ffeb3b' : 'inherit'
            }}
          >
            {import.meta.env.VITE_APP_NAME}
          </Typography>
          {!isMobile && (
            <>
              <Button color="inherit" component={Link} to="/tasks-list">Active Tasks</Button>
              <Button color="inherit" component={Link} to="/completed-tasks">Completed Tasks</Button>
              <Button color="inherit" component={Link} to="/categories">Categories</Button>
              <Button color="inherit" component={Link} to="/add-task">Add Task</Button>
              <Typography sx={{ ml: 2, mr: 2 }}>
                {user?.name || user?.userDetails}
              </Typography>
              <Button color="inherit" onClick={handleLogout}>
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
            top: 0,
            height: '100%'
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box sx={{ pt: { xs: '64px', sm: '80px' } }}>
        <Routes>
          <Route path="/" element={<Navigate to="/tasks-list" replace />} />
          <Route path="/tasks-list" element={<TaskListView />} />
          <Route path="/completed-tasks" element={<CompletedTasks />} />
          <Route path="/add-task" element={<TaskForm />} />
          <Route path="/edit-task/:id" element={<TaskForm />} />
          <Route path="/categories" element={<CategoryManagement />} />
        </Routes>
      </Box>
    </div>
  );
};

export default ProtectedApp;